/**
 * PeriodEditingDialog Component
 *
 * Two-column layout:
 *   Left  — form: subject, teacher, room, conflict warnings
 *   Right — live teacher suggestions (loads once subject is picked)
 *
 * Requirements: 5.5, 5.6, 5.7, 5.8, 5.9, 5.11, 11.4, 11.5, 15.9, 15.10, 18.9
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
  Stack,
  Paper,
  Chip,
  SelectChangeEvent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Avatar,
  LinearProgress,
  Tooltip,
  ListItemButton,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as TrophyIcon,
  Person as PersonIcon,
  Room as RoomIcon,
  Delete as DeleteIcon,
  AutoFixHigh as AutoFixIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTeachersDirect,
  getSubjectsDirect,
  getRoomsDirect,
  createTimetableRecordDirect,
  updateTimetableRecordDirect,
  deleteTimetableRecordDirect,
  getTimetableRecordsDirect,
  getSubjectsForClass,
} from '@timetable/api';
import type {
  TimetableRecordWithRelations,
  DayOfWeek,
  Teacher,
  ClassSectionWithRelations,
} from '@timetable/types';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../Toast';
import { LoadingSpinner } from '../LoadingSpinner';
import { queryKeys } from '../../lib/queryKeys';
import { detectAllConflicts } from '../../utils/conflictDetection';
import { useTeacherSuggestions } from '../../hooks/useMultiSectionTimetable';

// ============================================================================
// Types
// ============================================================================

export interface PeriodEditingDialogProps {
  open: boolean;
  onClose: () => void;
  sectionId: string;
  periodId: string;
  day: DayOfWeek;
  existingRecord?: TimetableRecordWithRelations;
  allRecords?: TimetableRecordWithRelations[];
  schedulePeriods?: import('@timetable/types').SchedulePeriod[];
  sections?: ClassSectionWithRelations[];
  onSave?: () => void;
  onDelete?: () => void;
  academicYear?: string;
  academicYearId?: string;
  onSubjectChange?: (subjectId: string | null) => void;
}

interface LocalConflict {
  type: 'teacher' | 'room' | 'qualification' | 'unassociated';
  severity: 'hard' | 'soft';
  message: string;
  resolution?: string;
}

// ============================================================================
// Helpers
// ============================================================================

const DAY_API_MAP: Record<DayOfWeek, DayOfWeek> = {
  MONDAY: 'MONDAY', TUESDAY: 'TUESDAY', WEDNESDAY: 'WEDNESDAY',
  THURSDAY: 'THURSDAY', FRIDAY: 'FRIDAY', SATURDAY: 'SATURDAY', SUNDAY: 'SUNDAY',
};

function toApiDay(day: DayOfWeek) { return day; }

const RANK_COLOR: Record<number, string> = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };

// ============================================================================
// Main Component
// ============================================================================

export function PeriodEditingDialog({
  open,
  onClose,
  sectionId,
  periodId,
  day,
  existingRecord,
  allRecords,
  schedulePeriods,
  sections,
  onSave,
  onDelete,
  academicYear,
  academicYearId,
  onSubjectChange,
}: PeriodEditingDialogProps) {
  const { schoolContext, user } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [subjectId, setSubjectId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Reset form on open
  useEffect(() => {
    if (open) {
      if (existingRecord) {
        setSubjectId(existingRecord.subject_id);
        setTeacherId(existingRecord.teacher_id);
        setRoomId(existingRecord.room?.id ?? '');
        onSubjectChange?.(existingRecord.subject_id || null);
      } else {
        setSubjectId('');
        setTeacherId('');
        setRoomId('');
        onSubjectChange?.(null);
      }
      setShowConfirmation(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingRecord]);

  // Derive classId from the sections prop for class-filtered subject lookup
  // sections comes from multiSectionData which is already loaded before the dialog opens
  const classId = useMemo(
    () => sections?.find(s => s.id === sectionId)?.class_id ?? null,
    [sections, sectionId]
  );

  // Only consider classId resolved once sections are available
  // If sections is non-empty but no match found, classId stays null (safe fallback)
  const sectionsLoaded = !!sections && sections.length > 0;

  // Data fetching
  const { data: teachers, isLoading: loadingTeachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || '' }),
    enabled: open && !!schoolContext,
  });

  const { data: subjects, isLoading: loadingSubjects } = useQuery({
    queryKey: classId
      ? queryKeys.classSubjects.forClass(classId, schoolContext || '')
      : queryKeys.subjects.list({ school_id: schoolContext || '' }),
    queryFn: () =>
      classId
        ? getSubjectsForClass(classId, schoolContext || '')
        : getSubjectsDirect({ school_id: schoolContext || '' }),
    // Wait until sections are loaded so classId is resolved before firing
    enabled: open && !!schoolContext && sectionsLoaded,
  });

  const { data: rooms, isLoading: loadingRooms } = useQuery({
    queryKey: queryKeys.rooms.list({ school_id: schoolContext || '' }),
    queryFn: () => getRoomsDirect({ school_id: schoolContext || '' }),
    enabled: open && !!schoolContext,
  });

  // Teacher suggestions — fires as soon as subjectId is set
  const { data: suggestionsData, isLoading: loadingSuggestions, isError: suggestionsError, error: suggestionsErrorObj } = useTeacherSuggestions(
    sectionId,
    periodId,
    toApiDay(day),
    subjectId || null,
  );
  const rawSuggestions = suggestionsData?.suggestions ?? [];

  // Fetch records for this specific day+period across the whole school (excluding current section).
  // This is a small, targeted query — not subject to the 1000-row school-wide cap.
  // Used to determine which teachers are already busy at this exact slot.
  const { data: slotRecords } = useQuery({
    queryKey: [...queryKeys.timetable.all, 'slot', schoolContext, day, periodId],
    queryFn: () => getTimetableRecordsDirect({
      school_id: schoolContext!,
      day_of_week: toApiDay(day),
      period_id: periodId,
    }),
    enabled: open && !!schoolContext && !!periodId && !!subjectId,
    staleTime: 10_000,
  });

  // Conflict detection
  const otherRecords = useMemo(
    () => (allRecords ?? []).filter(r => r.id !== existingRecord?.id),
    [allRecords, existingRecord?.id]
  );

  // Fetch the selected teacher's records across ALL classes to detect inter-class conflicts.
  // Only fires when a teacher is selected.
  const { data: teacherCrossClassRecords, isLoading: loadingCrossClass } = useQuery({
    queryKey: queryKeys.timetable.list({ teacher_id: teacherId, school_id: schoolContext || '' }),
    queryFn: () => getTimetableRecordsDirect({ teacher_id: teacherId, school_id: schoolContext || '' }),
    enabled: !!teacherId && !!schoolContext,
    staleTime: 30_000,
  });

  // True while cross-class conflict data is still being fetched for the selected teacher.
  const conflictCheckPending = !!teacherId && loadingCrossClass;

  // Merge cross-class records into otherRecords, skipping any already present (same class).
  const allOtherRecords = useMemo(() => {
    if (!teacherCrossClassRecords) return otherRecords;
    const existingIds = new Set(otherRecords.map(r => r.id));
    const crossClass = teacherCrossClassRecords.filter(
      r => !existingIds.has(r.id) && r.id !== existingRecord?.id
    );
    return [...otherRecords, ...crossClass];
  }, [otherRecords, teacherCrossClassRecords, existingRecord?.id]);

  // Annotate each suggestion with real availability based on existing records.
  // Uses slotRecords — a targeted query for this exact day+period across the school.
  const suggestions = useMemo(() => {
    // Build busy map from the targeted slot query — excludes current section to avoid self-conflict
    const busyTeacherMap = new Map<string, string>();
    for (const r of (slotRecords ?? [])) {
      if (r.class_section_id !== sectionId && r.id !== existingRecord?.id) {
        const className = r.class_section?.class?.class_name ?? '';
        const sectionName = r.class_section?.section ?? '';
        const label = sectionName ? `${className} ${sectionName}`.trim() : className;
        busyTeacherMap.set(r.teacher_id, label);
      }
    }

    return rawSuggestions.map(s => ({
      ...s,
      availability: busyTeacherMap.has(s.teacher.id) ? ('busy' as const) : ('available' as const),
      busyInClass: busyTeacherMap.get(s.teacher.id) ?? null,
      isAssociated: s.isAssociated,
    }));
  }, [rawSuggestions, slotRecords, sectionId, existingRecord?.id]);

  // Log suggestion errors to console for debugging
  if (suggestionsError) {
    console.error('[PeriodEditingDialog] suggestions error:', suggestionsErrorObj);
  }

  // Subject suggestions — ranked by how underrepresented they are in this section.
  // Subjects with fewer existing assignments in this section rank higher.
  const subjectSuggestions = useMemo(() => {
    if (!subjects) return [];
    const sectionRecords = (allRecords ?? []).filter(
      r => r.class_section_id === sectionId && r.id !== existingRecord?.id
    );
    const countMap = new Map<string, number>();
    for (const r of sectionRecords) {
      if (r.subject_id) countMap.set(r.subject_id, (countMap.get(r.subject_id) ?? 0) + 1);
    }
    return [...subjects]
      .map(s => ({ subject: s, count: countMap.get(s.id) ?? 0 }))
      .sort((a, b) => a.count - b.count);
  }, [subjects, allRecords, sectionId, existingRecord?.id]);

  const availableRooms = rooms?.filter(
    (room) => !allOtherRecords.some(
      (r) => {
        if (r.room?.id !== room.id) return false;
        if (r.day_of_week !== toApiDay(day)) return false;
        const targetPeriod = schedulePeriods?.find(p => p.id === periodId)
          ?? existingRecord?.period
          ?? allRecords?.find(rec => rec.period_id === periodId)?.period
          ?? null;
        const targetStart = targetPeriod?.time_start;
        const targetEnd = targetPeriod?.time_end;
        if (!targetStart || !targetEnd) return r.period_id === periodId;
        const rStart = r.period?.time_start;
        const rEnd = r.period?.time_end;
        if (!rStart || !rEnd) return false;
        return rStart < targetEnd && rEnd > targetStart;
      }
    )
  );

  // Derive whether the currently selected teacher is associated with this class.
  // `suggestions` already carries `isAssociated` from the API — no extra fetch needed.
  const selectedTeacherIsAssociated = useMemo(() => {
    if (!teacherId || suggestions.length === 0) return true; // unknown → don't warn
    const match = suggestions.find(s => s.teacher.id === teacherId);
    if (!match) return true; // teacher chosen from dropdown, not in suggestions → can't tell
    return match.isAssociated;
  }, [teacherId, suggestions]);

  const conflicts: LocalConflict[] = useMemo(() => {
    if (!teacherId || !subjectId) return [];
    const teacher = teachers?.find(t => t.id === teacherId);
    const subject = subjects?.find(s => s.id === subjectId);
    const room = rooms?.find(r => r.id === roomId) ?? null;
    const section = existingRecord?.class_section ?? sections?.find(s => s.id === sectionId) ?? null;
    const tempId = existingRecord?.id ?? '__new__';
    // Resolve the period's time window — needed for time-overlap conflict detection.
    // schedulePeriods is the most reliable source; fall back to existingRecord or allRecords.
    const resolvedPeriod = schedulePeriods?.find(p => p.id === periodId)
      ?? existingRecord?.period
      ?? allRecords?.find(r => r.period_id === periodId)?.period
      ?? null;

    if (!resolvedPeriod?.time_start || !resolvedPeriod?.time_end) {
      // Period time data unavailable — fall back to direct check matching suggestions panel logic.
      const apiDay = toApiDay(day);
      const directConflicts: LocalConflict[] = [];
      const conflictingRecord = allOtherRecords.find(
        r => r.teacher_id === teacherId &&
          r.day_of_week === apiDay &&
          r.id !== tempId &&
          r.period_id === periodId  // same period slot as fallback
      );
      if (conflictingRecord) {
        const teacherName = teacher?.name ?? 'Unknown Teacher';
        const periodNumber = conflictingRecord.period?.period_number ?? 0;
        directConflicts.push({
          type: 'teacher',
          severity: 'hard',
          message: `${teacherName} is already teaching another class on ${apiDay}, Period ${periodNumber} — select a different teacher`,
          resolution: 'Pick a different teacher from the suggestions on the right',
        });
      }
      if (!selectedTeacherIsAssociated) {
        const teacherName = teacher?.name ?? 'Unknown Teacher';
        directConflicts.push({
          type: 'unassociated',
          severity: 'soft',
          message: `${teacherName} is not assigned to this class in Teacher-Class Assignments`,
          resolution: 'You can still assign, or update Teacher-Class Assignments in Master Data first',
        });
      }
      return directConflicts;    }

    const tempRecord = {
      id: tempId,
      teacher_id: teacherId, subject_id: subjectId, room_id: roomId || null,
      period_id: periodId, day_of_week: toApiDay(day), class_section_id: sectionId,
      teacher: teacher ?? { id: teacherId, name: 'Unknown' },
      subject: subject ?? { id: subjectId, subject_name: 'Unknown' },
      room: room ? { id: room.id, room_name: room.name ?? room.id } : null,
      class_section: section, period: resolvedPeriod, is_supervision: false,
    } as any;

    // Use the same direct time-overlap approach as the suggestions panel — it's proven to work.
    const apiDay = toApiDay(day);
    const tStart = resolvedPeriod.time_start;
    const tEnd = resolvedPeriod.time_end;
    const result: LocalConflict[] = [];

    // Teacher conflict: any other record with same teacher, same day, overlapping time
    const teacherConflictRecord = allOtherRecords.find(r => {
      if (r.teacher_id !== teacherId) return false;
      if (r.day_of_week !== apiDay) return false;
      const rStart = r.period?.time_start;
      const rEnd = r.period?.time_end;
      if (!rStart || !rEnd) return r.period_id === periodId; // fallback
      return rStart < tEnd && rEnd > tStart;
    });
    if (teacherConflictRecord) {
      const teacherName = teacher?.name ?? 'Unknown Teacher';
      const periodNumber = resolvedPeriod.period_number ?? 0;
      result.push({
        type: 'teacher',
        severity: 'hard',
        message: `${teacherName} is already teaching another class on ${apiDay}, Period ${periodNumber} — select a different teacher`,
        resolution: 'Pick a different teacher from the suggestions on the right',
      });
    }

    // Room conflict: any other record with same room, same day, overlapping time
    if (roomId) {
      const roomConflictRecord = allOtherRecords.find(r => {
        if ((r.room as any)?.id !== roomId) return false;
        if (r.day_of_week !== apiDay) return false;
        const rStart = r.period?.time_start;
        const rEnd = r.period?.time_end;
        if (!rStart || !rEnd) return r.period_id === periodId;
        return rStart < tEnd && rEnd > tStart;
      });
      if (roomConflictRecord) {
        const roomName = room ? ((room as any).room_name ?? (room as any).name ?? 'Unknown Room') : 'Unknown Room';
        const periodNumber = resolvedPeriod.period_number ?? 0;
        result.push({
          type: 'room',
          severity: 'hard',
          message: `${roomName} is already booked on ${apiDay}, Period ${periodNumber} — select a different room`,
          resolution: 'Select a different room or leave it unassigned',
        });
      }
    }

    // Unassociated teacher warning (soft): teacher is not linked to this class
    // in Teacher-Class Assignments. Allow save but require confirmation.
    if (!selectedTeacherIsAssociated) {
      const teacherName = teacher?.name ?? 'Unknown Teacher';
      result.push({
        type: 'unassociated',
        severity: 'soft',
        message: `${teacherName} is not assigned to this class in Teacher-Class Assignments`,
        resolution: 'You can still assign, or update Teacher-Class Assignments in Master Data first',
      });
    }

    return result;
  }, [teacherId, subjectId, roomId, periodId, day, sectionId, teachers, subjects, rooms, allOtherRecords, existingRecord, sections, allRecords, schedulePeriods, selectedTeacherIsAssociated]);

  const hasHardConflicts = conflicts.some(c => c.severity === 'hard');

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!schoolContext) throw new Error('No school context');
      const data = {
        school_id: schoolContext,
        class_section_id: sectionId,
        period_id: periodId,
        day_of_week: toApiDay(day),
        teacher_id: teacherId,
        subject_id: subjectId,
        room_id: roomId || null,
        academic_year: academicYear ?? '',
        academic_year_id: academicYearId ?? undefined,
        valid_from: new Date().toISOString().split('T')[0],
        created_by: user?.id,
      };
      return existingRecord
        ? updateTimetableRecordDirect(existingRecord.id, data)
        : createTimetableRecordDirect(data);
    },
    onSuccess: () => {
      toast.success(existingRecord ? 'Period updated' : 'Period assigned');
      queryClient.invalidateQueries({ queryKey: queryKeys.timetable.all });
      onSave?.();
      handleClose();
    },
    onError: (error: any) => toast.error(error.message || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!existingRecord) throw new Error('No record to delete');
      return deleteTimetableRecordDirect(existingRecord.id);
    },
    onSuccess: () => {
      toast.success('Period deleted');
      queryClient.invalidateQueries({ queryKey: queryKeys.timetable.all });
      onDelete?.();
      handleClose();
    },
    onError: (error: any) => toast.error(error.message || 'Failed to delete'),
  });

  const handleClose = () => {
    setSubjectId(''); setTeacherId(''); setRoomId('');
    setShowConfirmation(false);
    onSubjectChange?.(null);
    onClose();
  };

  const handleSubjectChange = (e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    setSubjectId(val);
    setTeacherId(''); // reset teacher when subject changes
    setShowConfirmation(false);
    onSubjectChange?.(val || null);
  };

  const handleSave = () => {
    if (hasHardConflicts) { toast.error('Resolve all conflicts before saving'); return; }
    const softConflicts = conflicts.filter(c => c.severity === 'soft');
    if (softConflicts.length > 0 && !showConfirmation) { setShowConfirmation(true); return; }
    saveMutation.mutate();
  };

  const isFormValid = !!subjectId && !!teacherId && !!academicYear;
  const isLoading = loadingTeachers || loadingSubjects || loadingRooms;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="period-editing-dialog-title"
      PaperProps={{ sx: { height: '80vh' } }}
    >
      <DialogTitle id="period-editing-dialog-title" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {existingRecord ? 'Edit Period' : 'Add Period'}
          <Chip
            label={`${day.charAt(0) + day.slice(1).toLowerCase()}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <LoadingSpinner />
          </Box>
        ) : (
          <>
            {/* ── Left column: form ── */}
            <Box
              sx={{
                width: 340,
                flexShrink: 0,
                p: 2.5,
                overflowY: 'auto',
                borderRight: 1,
                borderColor: 'divider',
              }}
            >
              <Stack spacing={2.5}>
                {/* Subject */}
                <FormControl fullWidth required size="small">
                  <InputLabel>Subject *</InputLabel>
                  <Select value={subjectId} label="Subject *" onChange={handleSubjectChange}>
                    {subjects?.map(s => (
                      <MenuItem key={s.id} value={s.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color_code || 'grey.400', flexShrink: 0 }} />
                          {s.subject_name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Teacher — filtered to subject-qualified teachers only */}
                <FormControl fullWidth required size="small" disabled={!subjectId}>
                  <InputLabel>Teacher *</InputLabel>
                  <Select value={teacherId} label="Teacher *" onChange={e => { setTeacherId(e.target.value); setShowConfirmation(false); }}>
                    {(subjectId
                      ? teachers?.filter(t => t.subjects?.some(s => s.id === subjectId))
                      : teachers
                    )?.map(t => {
                      const suggestion = suggestions.find(s => s.teacher.id === t.id);
                      const notInClass = suggestion ? !suggestion.isAssociated : false;
                      return (
                        <MenuItem key={t.id} value={t.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ flex: 1 }}>{t.name}</Typography>
                            {notInClass && (
                              <Chip label="Not in class" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                {/* Room */}
                <FormControl fullWidth size="small">
                  <InputLabel>Room (optional)</InputLabel>
                  <Select value={roomId} label="Room (optional)" onChange={e => setRoomId(e.target.value)}>
                    <MenuItem value=""><em>No room</em></MenuItem>
                    {availableRooms?.map(r => (
                      <MenuItem key={r.id} value={r.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RoomIcon fontSize="small" color="action" />
                          {r.name}
                          {r.capacity && (
                            <Typography variant="caption" color="text.secondary">
                              (cap: {r.capacity})
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Conflicts */}
                {conflicts.length > 0 && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: hasHardConflicts ? 'error.50' : 'warning.50',
                      borderColor: hasHardConflicts ? 'error.main' : 'warning.main',
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color={hasHardConflicts ? 'error.main' : 'warning.main'}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
                    >
                      {hasHardConflicts ? <ErrorIcon fontSize="inherit" /> : <WarningIcon fontSize="inherit" />}
                      {hasHardConflicts ? 'Conflicts' : 'Warnings'}
                    </Typography>
                    <List dense disablePadding>
                      {conflicts.map((c, i) => (
                        <ListItem key={i} sx={{ px: 0, py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            {c.severity === 'hard'
                              ? <ErrorIcon color="error" sx={{ fontSize: 14 }} />
                              : <WarningIcon color="warning" sx={{ fontSize: 14 }} />}
                          </ListItemIcon>
                          <ListItemText
                            primary={c.message}
                            secondary={c.resolution}
                            primaryTypographyProps={{ variant: 'caption' }}
                            secondaryTypographyProps={{ variant: 'caption', color: 'text.disabled' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}

                {/* Soft conflict confirmation */}
                {showConfirmation && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      {conflicts.some(c => c.type === 'unassociated')
                        ? 'This teacher is not in Teacher-Class Assignments for this class. Click Assign again to proceed anyway.'
                        : 'Warnings exist. Click Assign again to proceed anyway.'}
                    </Typography>
                  </Alert>
                )}

                {/* Conflict check in progress */}
                {conflictCheckPending && isFormValid && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    <Typography variant="caption">Checking for conflicts…</Typography>
                  </Alert>
                )}

                {/* All good */}
                {isFormValid && !conflictCheckPending && conflicts.length === 0 && (
                  <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />} sx={{ py: 0.5 }}>
                    <Typography variant="caption">No conflicts — ready to save.</Typography>
                  </Alert>
                )}
              </Stack>
            </Box>

            {/* ── Right column: suggestions ── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {!subjectId ? 'Subject Suggestions' : 'Teacher Suggestions'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {!subjectId
                    ? 'Ranked by least assigned in this section — click to select'
                    : 'Ranked by availability & workload — click to select'}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {/* ── Subject suggestions (no subject selected yet) ── */}
                {!subjectId ? (
                  subjectSuggestions.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                      <Typography variant="body2" color="text.disabled" textAlign="center">No subjects found</Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {subjectSuggestions.map(({ subject: s, count }, idx) => (
                        <ListItemButton
                          key={s.id}
                          onClick={() => {
                            setSubjectId(s.id);
                            setTeacherId('');
                            onSubjectChange?.(s.id);
                          }}
                          sx={{ gap: 1.5, borderBottom: 1, borderColor: 'divider', py: 1.25, px: 2 }}
                        >
                          {/* Color dot */}
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.color_code || 'grey.400', flexShrink: 0 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{s.subject_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {count === 0 ? 'Not yet assigned this week' : `${count} period${count !== 1 ? 's' : ''} assigned this week`}
                            </Typography>
                          </Box>
                          {/* Rank */}
                          {idx < 3 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: RANK_COLOR[idx + 1], fontWeight: 700, fontSize: 12 }}>
                              <TrophyIcon sx={{ fontSize: 14, color: RANK_COLOR[idx + 1] }} />
                              #{idx + 1}
                            </Box>
                          )}
                          {/* Period count badge */}
                          <Chip
                            label={count === 0 ? 'None' : `×${count}`}
                            size="small"
                            color={count === 0 ? 'success' : count <= 2 ? 'warning' : 'default'}
                            sx={{ fontWeight: 700, fontSize: 11, height: 20 }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )
                ) : loadingSuggestions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : suggestionsError ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1, p: 3 }}>
                    <Typography variant="body2" color="error" textAlign="center">
                      Could not load suggestions. Check that teachers are assigned to this subject in master data.
                    </Typography>
                    <Typography variant="caption" color="text.disabled" textAlign="center">
                      {(suggestionsErrorObj as any)?.message}
                    </Typography>
                  </Box>
                ) : suggestions.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1, color: 'text.disabled', p: 3 }}>
                    <Typography variant="body2" textAlign="center">No qualified teachers found for this subject</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {/* Sort: available first, busy last */}
                    {[...suggestions]
                      .sort((a, b) => {
                        if (a.availability === b.availability) return a.rank - b.rank;
                        return a.availability === 'available' ? -1 : 1;
                      })
                      .map((s) => {
                      const isSelected = teacherId === s.teacher.id;
                      const isBusy = s.availability === 'busy';
                      const isUnassociated = !s.isAssociated;
                      return (
                        <ListItemButton
                          key={s.teacher.id}
                          selected={isSelected}
                          onClick={() => { setTeacherId(s.teacher.id); setShowConfirmation(false); }}
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 0.75,
                            borderBottom: 1,
                            borderColor: 'divider',
                            py: 1.5,
                            px: 2,
                            opacity: isBusy ? 0.55 : 1,
                            ...(isSelected && { bgcolor: 'primary.50' }),
                            ...(isBusy && { bgcolor: 'grey.50' }),
                          }}
                        >
                          {/* Name row */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: isBusy ? 'grey.400' : isSelected ? 'primary.main' : 'grey.500' }}>
                              {s.teacher.name.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                              {s.teacher.name}
                            </Typography>
                            {/* Busy badge */}
                            {isBusy && (
                              <Chip label="Busy" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                            )}
                            {/* Not-in-class badge */}
                            {!isBusy && isUnassociated && (
                              <Tooltip title="Not in Teacher-Class Assignments for this class" arrow>
                                <Chip label="Not in class" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                              </Tooltip>
                            )}
                            {/* Rank badge */}
                            {!isBusy && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: RANK_COLOR[s.rank] ?? 'text.secondary', fontWeight: 700, fontSize: 12 }}>
                                {s.rank <= 3 && <TrophyIcon sx={{ fontSize: 14, color: RANK_COLOR[s.rank] }} />}
                                #{s.rank}
                              </Box>
                            )}
                            <Chip
                              label={`${s.score}%`}
                              size="small"
                              color={isBusy ? 'default' : s.score >= 80 ? 'success' : s.score >= 60 ? 'warning' : 'default'}
                              sx={{ fontWeight: 700, fontSize: 11, height: 20 }}
                            />
                          </Box>

                          {/* Workload bar */}
                          <Tooltip title={`${s.workload.currentPeriods}/${s.workload.maxPeriods} periods`} arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <LinearProgress
                                variant="determinate"
                                value={s.workload.percentage}
                                sx={{
                                  flex: 1, height: 5, borderRadius: 3,
                                  bgcolor: 'rgba(0,0,0,0.08)',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: isBusy ? 'grey.400' : s.workload.percentage < 50 ? 'success.main' : s.workload.percentage < 80 ? 'warning.main' : 'error.main',
                                  },
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: 'right' }}>
                                {s.workload.currentPeriods}/{s.workload.maxPeriods}
                              </Typography>
                            </Box>
                          </Tooltip>

                          {/* Busy reason */}
                          {isBusy ? (
                            <Typography variant="caption" color="error.main">
                              {s.busyInClass ? `Already assigned to ${s.busyInClass} at this time` : 'Already assigned to another class at this time'}
                            </Typography>
                          ) : s.reasons.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {s.reasons.join(' · ')}
                            </Typography>
                          )}

                          {/* Subject chips */}
                          {s.qualifications?.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {s.qualifications.map(q => (
                                <Chip
                                  key={q.id}
                                  label={q.subject_name}
                                  size="small"
                                  sx={{ bgcolor: q.color_code || '#e0e0e0', color: '#000', fontSize: 10, height: 18 }}
                                />
                              ))}
                            </Box>
                          )}
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        {existingRecord && (
          <Button
            onClick={() => deleteMutation.mutate()}
            color="error"
            startIcon={deleteMutation.isPending ? <CircularProgress size={14} /> : <DeleteIcon />}
            disabled={deleteMutation.isPending || saveMutation.isPending}
            sx={{ mr: 'auto' }}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        )}
        <Button onClick={handleClose} color="inherit">Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isFormValid || hasHardConflicts || conflictCheckPending || saveMutation.isPending}
          startIcon={saveMutation.isPending || conflictCheckPending ? <CircularProgress size={14} /> : undefined}
        >
          {saveMutation.isPending ? 'Saving…' : conflictCheckPending ? 'Checking…' : existingRecord ? 'Update' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
